import { ReactNode } from 'react'
import { useFeatureGate, Feature, Tier } from '@/hooks/useFeatureGate'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Lock, ArrowRight, Sparkles } from 'lucide-react'
import { TierBadge } from '@/components/ui/TierBadge'
import { useNavigate } from 'react-router-dom'

interface FeatureGateProps {
  feature: Feature
  children: ReactNode
  /** Override the full-page upsell copy. Defaults read from the feature slug. */
  title?: string
  description?: string
}

const FEATURE_TITLES: Partial<Record<Feature, { title: string; description: string }>> = {
  ai_inbound_agent: {
    title: 'AI voice agent is a Partner feature',
    description: 'Upgrade to Partner to add a phone number, create voice agents, and let your AI answer every inbound call 24/7.',
  },
  phone_number: {
    title: 'Phone numbers are a Partner feature',
    description: 'Upgrade to Partner to provision a dedicated number or route your existing line to your AI.',
  },
  call_transcripts: {
    title: 'Call transcripts are a Partner feature',
    description: 'Upgrade to Partner to record, transcribe, and search every inbound call.',
  },
  ai_outbound_agent: {
    title: 'Outbound calling is a Principal feature',
    description: 'Upgrade to Principal to get speed-to-lead: the AI calls web-form leads back instantly and re-engages callers who dropped off mid-intake. Fewer lost leads, faster contact.',
  },
  auto_callback: {
    title: 'Auto-callback is a Principal feature',
    description: 'Upgrade to Principal so dropped inbound calls and mid-intake hang-ups get automatically re-dialed on a smart retry schedule. No lead falls through the cracks.',
  },
  crm_integrations: {
    title: 'CRM integrations are a Principal feature',
    description: 'Upgrade to Principal to sync leads, bookings, and calls with Clio, Lawmatics, HubSpot, and more.',
  },
}

/**
 * Soft-gate wrapper: if the current org has the feature, render children
 * verbatim. If not, render a full-page upsell panel that deep-links into
 * the billing page. Use this at the top of any page that is effectively
 * useless without a higher tier.
 */
export function FeatureGate({ feature, children, title, description }: FeatureGateProps) {
  const { isAllowed, requiredTier, loading } = useFeatureGate()
  const navigate = useNavigate()

  // While loading, show nothing rather than flashing the upsell. The backend
  // is the real gate anyway — the worst case of rendering children early is
  // a 402 toast/modal, which is already handled globally.
  if (loading) return null

  if (isAllowed(feature)) {
    return <>{children}</>
  }

  const tier: Tier = (requiredTier(feature) ?? 'partner') as Tier
  const copy = FEATURE_TITLES[feature]
  const displayTitle = title ?? copy?.title ?? `This feature requires ${tier}`
  const displayDesc = description ?? copy?.description ?? `Upgrade to ${tier} to unlock.`

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <Card>
        <CardContent className="py-10 px-6 sm:px-10">
          <div className="flex items-start gap-4">
            <div className="shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/10 to-brand-periwinkle/10 border border-brand-royal/20 flex items-center justify-center">
              <Lock className="h-6 w-6 text-brand-royal" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Locked</p>
                <TierBadge tier={tier} size="sm" />
              </div>
              <h2 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">
                {displayTitle}
              </h2>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                {displayDesc}
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                <Button
                  onClick={() => navigate('/settings/billing')}
                  className="bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 text-white gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  Upgrade to <span className="capitalize">{tier}</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={() => navigate('/')}>
                  Back to dashboard
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
